import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Check, User, Stethoscope, Scan, Building2,
  ClipboardCheck, Search, Loader2, Save, Lock, Plus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";

const STEPS = [
  { id: 0, label: "Patient", icon: User },
  { id: 1, label: "Clinical", icon: Stethoscope },
  { id: 2, label: "Examination", icon: Scan },
  { id: 3, label: "Centre", icon: Building2 },
  { id: 4, label: "Review", icon: ClipboardCheck },
];

function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  const diff = Date.now() - d.getTime();
  const age = Math.floor(diff / (365.25 * 86400000));
  return age >= 0 ? String(age) : "";
}

export default function CreateReferral() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { subscription } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [studies, setStudies] = useState({});
  const [centres, setCentres] = useState([]);
  const [centreSearch, setCentreSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [patient, setPatient] = useState({ first_name: "", last_name: "", dob: "", age: "", gender: "", phone: "", email: "", address: "" });
  const [clinical, setClinical] = useState({ clinical_history: "", working_diagnosis: "", previous_imaging: "", allergies: "", chronic_illness: "", pregnancy_status: "", lmp: "" });
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [priority, setPriority] = useState("Routine");
  const [contrast, setContrast] = useState("Not Applicable");
  const [centreId, setCentreId] = useState(null);
  const [newStudyCat, setNewStudyCat] = useState("X-Ray");
  const [newStudyName, setNewStudyName] = useState("");
  const [addingStudy, setAddingStudy] = useState(false);

  const locked = subscription && !subscription.can_create;

  useEffect(() => {
    api.get("/studies").then((r) => setStudies(r.data));
    api.get("/centres").then((r) => setCentres(r.data));
    const tid = params.get("template");
    if (tid) {
      api.get("/templates").then((r) => {
        const t = r.data.find((x) => x.id === tid);
        if (t) {
          setSelectedStudies(t.studies || []);
          setPriority(t.priority || "Routine");
          setContrast(t.contrast || "Not Applicable");
          if (t.clinical) setClinical((c) => ({ ...c, ...t.clinical }));
          toast.success(`Template "${t.name}" applied`);
        }
      });
    }
    if (id) {
      api.get(`/referrals/${id}`).then((r) => {
        const d = r.data;
        setPatient(d.patient); setClinical(d.clinical); setSelectedStudies(d.studies);
        setPriority(d.priority); setContrast(d.contrast); setCentreId(d.centre_id);
      });
    } else {
      const pre = localStorage.getItem("referu_prefill_patient");
      if (pre) {
        try {
          const p = JSON.parse(pre);
          setPatient((prev) => ({ ...prev, first_name: p.first_name || "", last_name: p.last_name || "", dob: p.dob || "", age: p.age || calcAge(p.dob), gender: p.gender || "", phone: p.phone || "", email: p.email || "", address: p.address || "" }));
        } catch (e) {}
        localStorage.removeItem("referu_prefill_patient");
      }
    }
  }, []);

  const setP = (k) => (e) => {
    const v = e.target.value;
    setPatient((p) => ({ ...p, [k]: v, ...(k === "dob" ? { age: calcAge(v) } : {}) }));
  };
  const setC = (k) => (e) => setClinical((c) => ({ ...c, [k]: e.target.value }));

  const toggleStudy = (category, name) => {
    setSelectedStudies((prev) => {
      const exists = prev.find((s) => s.name === name && s.category === category);
      return exists ? prev.filter((s) => !(s.name === name && s.category === category)) : [...prev, { category, name }];
    });
  };
  const isSelected = (category, name) => selectedStudies.some((s) => s.name === name && s.category === category);

  const addCustomStudy = async () => {
    const name = newStudyName.trim();
    if (!name) return toast.error("Enter a study name");
    setAddingStudy(true);
    try {
      await api.post("/studies", { category: newStudyCat, name });
      const { data } = await api.get("/studies");
      setStudies(data);
      if (!isSelected(newStudyCat, name)) toggleStudy(newStudyCat, name);
      setNewStudyName("");
      toast.success(`"${name}" added`);
    } catch (e) {
      toast.error("Could not add study");
    } finally {
      setAddingStudy(false);
    }
  };

  const filteredCentres = centres.filter((c) => c.name.toLowerCase().includes(centreSearch.toLowerCase()));
  const selectedCentre = centres.find((c) => c.id === centreId);

  const canNext = () => {
    if (step === 0) return patient.first_name.trim().length > 0;
    if (step === 2) return selectedStudies.length > 0;
    return true;
  };

  const save = async (asDraft) => {
    setSaving(true);
    try {
      const payload = { patient, clinical, studies: selectedStudies, priority, contrast, centre_id: centreId, status: asDraft ? "draft" : "pending" };
      const res = id ? await api.put(`/referrals/${id}`, payload) : await api.post("/referrals", payload);
      toast.success(asDraft ? "Draft saved" : "Referral created");
      navigate(`/referrals/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 402) { toast.error("Subscription expired"); navigate("/subscription"); }
      else toast.error(err.response?.data?.detail || "Could not save referral");
    } finally {
      setSaving(false);
    }
  };

  if (locked) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-amber-200 p-10 text-center mt-10">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6 text-amber-600" /></div>
          <h2 className="text-2xl font-semibold text-slate-900 font-head">Subscription required</h2>
          <p className="text-slate-500 mt-2">Your free trial or subscription has ended. Renew to create new referrals.</p>
          <Button data-testid="locked-upgrade-btn" onClick={() => navigate("/subscription")} className="mt-6 rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">Upgrade now</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto pb-40">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">{id ? "Edit Referral" : "New Referral"}</h1>

      <div className="flex items-center justify-between mt-6 mb-8 overflow-x-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button onClick={() => i < step && setStep(i)} className="flex flex-col items-center gap-1.5 shrink-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${step === i ? "bg-blue-600 text-white" : step > i ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                {step > i ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs font-medium ${step === i ? "text-blue-600" : "text-slate-400"}`}>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > i ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            <div><Label>First Name *</Label><Input data-testid="patient-first" value={patient.first_name} onChange={setP("first_name")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Last Name</Label><Input data-testid="patient-last" value={patient.last_name} onChange={setP("last_name")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Date of Birth</Label><Input data-testid="patient-dob" type="date" value={patient.dob || ""} onChange={setP("dob")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Age</Label><Input data-testid="patient-age" value={patient.age || ""} readOnly className="mt-1.5 h-12 rounded-xl bg-slate-50" placeholder="auto" /></div>
            <div>
              <Label>Gender</Label>
              <Select value={patient.gender} onValueChange={(v) => setPatient((p) => ({ ...p, gender: v }))}>
                <SelectTrigger data-testid="patient-gender" className="mt-1.5 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input data-testid="patient-phone" value={patient.phone} onChange={setP("phone")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="patient-email" value={patient.email} onChange={setP("email")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Address</Label><Input data-testid="patient-address" value={patient.address} onChange={setP("address")} className="mt-1.5 h-12 rounded-xl" /></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <div><Label>Clinical History</Label><Textarea data-testid="clin-history" value={clinical.clinical_history} onChange={setC("clinical_history")} className="mt-1.5 rounded-xl min-h-[90px]" /></div>
            <div className="grid md:grid-cols-2 gap-5">
              <div><Label>Working Diagnosis</Label><Input data-testid="clin-diagnosis" value={clinical.working_diagnosis} onChange={setC("working_diagnosis")} className="mt-1.5 h-12 rounded-xl" /></div>
              <div><Label>Relevant Previous Imaging</Label><Input data-testid="clin-prev" value={clinical.previous_imaging} onChange={setC("previous_imaging")} className="mt-1.5 h-12 rounded-xl" /></div>
              <div><Label>Allergies</Label><Input data-testid="clin-allergies" value={clinical.allergies} onChange={setC("allergies")} className="mt-1.5 h-12 rounded-xl" /></div>
              <div><Label>Chronic Illness</Label><Input data-testid="clin-chronic" value={clinical.chronic_illness} onChange={setC("chronic_illness")} className="mt-1.5 h-12 rounded-xl" /></div>
            </div>
            {patient.gender === "Female" && (
              <div className="grid md:grid-cols-2 gap-5 bg-blue-50/50 rounded-xl p-4">
                <div>
                  <Label>Pregnancy Status</Label>
                  <Select value={clinical.pregnancy_status} onValueChange={(v) => setClinical((c) => ({ ...c, pregnancy_status: v }))}>
                    <SelectTrigger data-testid="clin-pregnancy" className="mt-1.5 h-12 rounded-xl bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not pregnant">Not pregnant</SelectItem>
                      <SelectItem value="Pregnant">Pregnant</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>LMP</Label><Input data-testid="clin-lmp" type="date" value={clinical.lmp} onChange={setC("lmp")} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <Accordion type="multiple" className="space-y-2">
              {Object.entries(studies).map(([cat, items]) => (
                <AccordionItem key={cat} value={cat} className="border border-slate-200 rounded-xl px-4">
                  <AccordionTrigger data-testid={`cat-${cat}`} className="hover:no-underline">
                    <span className="font-semibold text-slate-800">{cat} <span className="text-xs text-slate-400 ml-2">{items.length}</span></span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid sm:grid-cols-2 gap-2 pb-2">
                      {items.map((it) => (
                        <label key={it.id} data-testid={`study-${it.name.replace(/[^a-zA-Z0-9]/g, "-")}`} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${isSelected(cat, it.name) ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                          <Checkbox checked={isSelected(cat, it.name)} onCheckedChange={() => toggleStudy(cat, it.name)} />
                          <span className="text-sm text-slate-700">{it.name}</span>
                        </label>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-4 border border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/60">
              <p className="text-sm font-medium text-slate-700 mb-2">Can't find a study? Add your own</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={newStudyCat} onValueChange={setNewStudyCat}>
                  <SelectTrigger data-testid="custom-study-cat" className="h-11 rounded-xl sm:w-44 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(studies).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="custom-study-name" value={newStudyName} onChange={(e) => setNewStudyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomStudy())}
                  placeholder="e.g. CT Temporal Bones" className="h-11 rounded-xl flex-1 bg-white" />
                <Button data-testid="add-custom-study-btn" type="button" onClick={addCustomStudy} disabled={addingStudy} variant="outline" className="h-11 rounded-xl border-slate-300">
                  {addingStudy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                </Button>
              </div>
            </div>

            {selectedStudies.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedStudies.map((s) => (
                  <span key={s.name} className="text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-full">{s.name}</span>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <Label className="mb-2 block">Priority</Label>
                <RadioGroup value={priority} onValueChange={setPriority} className="space-y-2">
                  {["Routine", "Urgent", "STAT"].map((p) => (
                    <label key={p} data-testid={`priority-${p}`} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${priority === p ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
                      <RadioGroupItem value={p} /> <span className="text-sm font-medium">{p}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label className="mb-2 block">Contrast</Label>
                <RadioGroup value={contrast} onValueChange={setContrast} className="space-y-2">
                  {["With Contrast", "Without Contrast", "As Per Radiologist", "Not Applicable"].map((p) => (
                    <label key={p} data-testid={`contrast-${p.replace(/\s+/g, "-")}`} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${contrast === p ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
                      <RadioGroupItem value={p} /> <span className="text-sm font-medium">{p}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="centre-search" value={centreSearch} onChange={(e) => setCentreSearch(e.target.value)} placeholder="Search radiology centres…" className="pl-10 h-12 rounded-xl" />
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto referu-scroll">
              {filteredCentres.map((c) => (
                <button key={c.id} data-testid={`centre-${c.id}`} onClick={() => setCentreId(c.id)} className={`w-full text-left p-4 rounded-xl border transition-colors ${centreId === c.id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">{c.name}</p>
                    {centreId === c.id && <Check className="w-5 h-5 text-blue-600" />}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{c.address}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{c.phone} · {c.email}</p>
                </button>
              ))}
              {filteredCentres.length === 0 && <p className="text-center text-slate-400 py-8">No centres found</p>}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <ReviewBlock title="Patient">
              <Row k="Name" v={`${patient.first_name} ${patient.last_name}`} />
              <Row k="DOB / Age" v={`${patient.dob || "—"} (${patient.age || "—"})`} />
              <Row k="Gender" v={patient.gender || "—"} />
              <Row k="Phone" v={patient.phone || "—"} />
            </ReviewBlock>
            <ReviewBlock title="Examination">
              <Row k="Studies" v={selectedStudies.map((s) => s.name).join(", ") || "—"} />
              <Row k="Priority" v={priority} />
              <Row k="Contrast" v={contrast} />
            </ReviewBlock>
            <ReviewBlock title="Clinical">
              <Row k="History" v={clinical.clinical_history || "—"} />
              <Row k="Diagnosis" v={clinical.working_diagnosis || "—"} />
            </ReviewBlock>
            <ReviewBlock title="Radiology Centre">
              <Row k="Centre" v={selectedCentre?.name || "Not selected"} />
            </ReviewBlock>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white border-t border-slate-200 p-4 flex items-center justify-between gap-3">
        <Button data-testid="back-btn" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-xl h-12 px-5">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Button data-testid="save-draft-btn" variant="outline" disabled={saving} onClick={() => save(true)} className="rounded-xl h-12 px-5 border-slate-300">
            <Save className="w-4 h-4 mr-1" /> Draft
          </Button>
          {step < 4 ? (
            <Button data-testid="next-btn" disabled={!canNext()} onClick={() => setStep(step + 1)} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button data-testid="generate-btn" disabled={saving} onClick={() => save(false)} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Referral <Check className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-3">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex gap-4 text-sm">
      <span className="text-slate-400 w-28 shrink-0">{k}</span>
      <span className="text-slate-800 font-medium">{v}</span>
    </div>
  );
}
